const arraysSum = (arr1, arr2) => {
    return arr1.map((a, i) => a + arr2[i]);
};

export const arraysSubtract = (arr1, arr2) => {
    return arr1.map((a, i) => a - arr2[i]);
};

export const arrayScale = (arr1, scaleFactor) => {
    return arr1.map((a, index) => scaleFactor[index] * a);
};


export const arePolygonsSimilar = (polygon1, polygon2) => {
    if (!polygon1?.length > 0 || !polygon2?.length > 0) return false;
    const v1 = polygon1.slice(0, 3);
    const v2 = polygon2.slice(0, 3);
    const translationVector = arraysSubtract(v2, v1);
    const mappedPolygon1 = polygon1.map((coordinate, index) => {
        const i = index % 3;
        return coordinate + translationVector[i];
    });
    const difference = arraysSubtract(mappedPolygon1, polygon2);
    return difference.every((e) => Math.abs(e) < 0.05);
};

export const calculateDistance = (point1, point2) => {
  return Math.sqrt(
    Math.pow(point1[0] - point2[0], 2) +
      Math.pow(point1[1] - point2[1], 2) +
      Math.pow(point1[2] - point2[2], 2)
  );
};

export const calculateEdgeLength = (edge) => {
  return calculateDistance(
    [edge[0].x, edge[0].y, edge[0].z],
    [edge[1].x, edge[1].y, edge[1].z]
  );
};

export const countPolygonEdges = (polygonString) => {
  try {
    if (!polygonString || typeof polygonString !== "string") {
      return 0;
    }

    const coordsString = polygonString.replace(/[[\]]/g, "");
    const coords = coordsString.split(",").map(Number);

    if (coords.some(isNaN)) {
      return 0;
    }

    const points = [];
    for (let i = 0; i < coords.length; i += 3) {
      if (i + 2 < coords.length) {
        points.push({
          x: coords[i],
          y: coords[i + 1],
          z: coords[i + 2],
        });
      }
    }

    const uniquePoints = [];
    const tolerance = 1e-10;

    for (let i = 0; i < points.length; i++) {
      const current = points[i];
      const isDuplicate = uniquePoints.some(
        (existingPoint) =>
          Math.abs(existingPoint.x - current.x) < tolerance &&
          Math.abs(existingPoint.y - current.y) < tolerance &&
          Math.abs(existingPoint.z - current.z) < tolerance
      );

      if (!isDuplicate) {
        uniquePoints.push(current);
      }
    }

    return uniquePoints.length;
  } catch (error) {
    return 0;
  }
};

export const getPolygonTypeName = (edgeCount) => {
  const typeNames = {
    3: "Triangle",
    4: "Rectangle",
    5: "Pentagon",
    6: "Hexagon",
    7: "Heptagon",
    8: "Octagon",
    9: "Nonagon",
    10: "Decagon",
  };

  if (edgeCount in typeNames) {
    return typeNames[edgeCount];
  } else if (edgeCount > 10) {
    return `${edgeCount}-sided Polygon`;
  } else {
    return "Unknown Shape";
  }
};

export const calculateWindowDimensions = (polygon) => {
  try {
    let coords;
    if (typeof polygon === "string") {
      coords = JSON.parse(polygon);
    } else if (Array.isArray(polygon)) {
      coords = polygon;
    } else {
      return { width: 0, height: 0, area: 0 };
    }

    if (!coords || coords?.length < 6) return { width: 0, height: 0, area: 0 };

    const points = [];
    for (let i = 0; i < coords?.length; i += 3) {
      if (i + 2 < coords?.length) {
        points.push({
          x: coords[i],
          y: coords[i + 1],
          z: coords[i + 2],
        });
      }
    }

    if (points.length < 3) return { width: 0, height: 0, area: 0 };

    let width = null;
    let height = null;

    const edge1 = {
      length: calculateEdgeLength([points[0], points[1]]),
      zDiff: Math.abs(points[1].z - points[0].z),
    };

    const edge2 = {
      length: calculateEdgeLength([points[1], points[2]]),
      zDiff: Math.abs(points[2].z - points[1].z),
    };

    if (Math.round(edge1.zDiff) === 0) {
      width = edge1.length;
      height = edge2.length;
    } else {
      width = edge2.length;
      height = edge1.length;
    }

    return { width, height };
  } catch (error) {
    return { width: 0, height: 0 };
  }
};

export const clusterWindowsByPolygonType = (allWindows, rectangleK = 20) => {
  const polygonGroups = {};
  const dimensionData = {};

  const processedWindows = new Set();

  allWindows.forEach((window) => {
    if (window.polygon) {
      const windowId = window.id || window.polygon;

      if (processedWindows.has(windowId)) {
        return;
      }
      processedWindows.add(windowId);

      const edgeCount = countPolygonEdges(window.polygon);
      const polygonType = getPolygonTypeName(edgeCount);

      if (!polygonGroups[edgeCount]) {
        polygonGroups[edgeCount] = {
          edgeCount,
          typeName: polygonType,
          windows: [],
          count: 0,
        };
      }

      polygonGroups[edgeCount].windows.push(window);
      polygonGroups[edgeCount].count++;

      if (edgeCount === 4) {
        const { width, height } = calculateWindowDimensions(window.polygon);
        if (width > 0 && height > 0) {
          if (!dimensionData[edgeCount]) {
            dimensionData[edgeCount] = [];
          }
          dimensionData[edgeCount].push({ width, height, window });
        }
      }
    }
  });

  const clusters = [];

  Object.values(polygonGroups).forEach((group) => {
    if (
      group.edgeCount === 4 &&
      dimensionData[4] &&
      dimensionData[4].length > 0
    ) {
      const dimensions = dimensionData[4].map((item) => ({
        width: item.width,
        height: item.height,
      }));
      const kMeansResult = kMeansClusteringDimensions(dimensions, rectangleK);

      kMeansResult.clusters.forEach((cluster, index) => {
        const clusterWindows = [];
        const processedWindowIds = new Set();

        cluster.members.forEach((member) => {
          dimensionData[4].forEach((item) => {
            if (
              Math.abs(item.width - member.width) < 0.1 &&
              Math.abs(item.height - member.height) < 0.1
            ) {
              const windowId = item.window.id || item.window.polygon;
              if (!processedWindowIds.has(windowId)) {
                processedWindowIds.add(windowId);
                clusterWindows.push(item.window);
              }
            }
          });
        });

        if (clusterWindows.length > 0) {
          clusters.push({
            edgeCount: 4,
            typeName: "Rectangle",
            subType: "dimensional",
            label: cluster.label,
            centroid: cluster.centroid,
            count: clusterWindows.length,
            windows: clusterWindows,
            minWidth: cluster.minWidth,
            maxWidth: cluster.maxWidth,
            minHeight: cluster.minHeight,
            maxHeight: cluster.maxHeight,
            isKMeansCluster: true,
            area: cluster.centroid.width * cluster.centroid.height,
          });
        }
      });
    } else {
      clusters.push({
        edgeCount: group.edgeCount,
        typeName: group.typeName,
        subType: "polygon",
        label: `${group.typeName} (${group.edgeCount} edges)`,
        count: group.count,
        windows: group.windows,
        isKMeansCluster: false,
      });
    }
  });

  return {
    clusters: clusters.sort((a, b) => {
      if (a.edgeCount !== b.edgeCount) {
        return a.edgeCount - b.edgeCount;
      }

      if (
        a.edgeCount === 4 &&
        b.edgeCount === 4 &&
        a.isKMeansCluster &&
        b.isKMeansCluster
      ) {
        const areaDiff = b.area - a.area;
        if (areaDiff !== 0) return areaDiff;

        const widthDiff = b.maxWidth - a.maxWidth;
        if (widthDiff !== 0) return widthDiff;

        const heightDiff = b.maxHeight - a.maxHeight;
        return heightDiff;
      }

      return b.count - a.count;
    }),
    polygonGroups,
  };
};

export const kMeansClusteringDimensions = (dimensions, k) => {
  if (!Array.isArray(dimensions) || dimensions.length === 0) {
    return { clusters: [], centroids: [] };
  }

  const uniqueDimensions = [];
  const tolerance = 1e-1;

  for (let i = 0; i < dimensions.length; i++) {
    const current = dimensions[i];
    const isDuplicate = uniqueDimensions.some(
      (existing) =>
        Math.abs(existing.width - current.width) < tolerance &&
        Math.abs(existing.height - current.height) < tolerance
    );

    if (!isDuplicate) {
      uniqueDimensions.push(current);
    }
  }

  if (uniqueDimensions.length <= 1) {
    return {
      clusters: [
        {
          centroid: uniqueDimensions[0] || { width: 0, height: 0 },
          members: dimensions,
          count: dimensions.length,
          label: formatDimensionLabel(
            uniqueDimensions[0] || { width: 0, height: 0 }
          ),
          minWidth: uniqueDimensions[0]?.width,
          maxWidth: uniqueDimensions[0]?.width,
          minHeight: uniqueDimensions[0]?.height,
          maxHeight: uniqueDimensions[0]?.height,
        },
      ],
      centroids: [uniqueDimensions[0] || { width: 0, height: 0 }],
    };
  }

  const reasonableK = Math.min(k, uniqueDimensions.length);

  let centroids = initializeCentroids2D(uniqueDimensions, reasonableK);

  let clusters = [];
  let iterations = 0;
  const maxIterations = 100;
  let converged = false;

  while (!converged && iterations < maxIterations) {
    clusters = Array.from({ length: reasonableK }, () => []);

    for (const dim of dimensions) {
      let nearestCentroidIndex = 0;
      let minDistance = distance2D(dim, centroids[0]);

      for (let i = 1; i < centroids.length; i++) {
        const dist = distance2D(dim, centroids[i]);
        if (dist < minDistance) {
          minDistance = dist;
          nearestCentroidIndex = i;
        }
      }

      clusters[nearestCentroidIndex].push(dim);
    }

    const newCentroids = clusters.map((cluster) => {
      if (cluster.length === 0) return { width: 0, height: 0 };

      const avgWidth =
        cluster.reduce((sum, dim) => sum + dim.width, 0) / cluster.length;
      const avgHeight =
        cluster.reduce((sum, dim) => sum + dim.height, 0) / cluster.length;

      return { width: avgWidth, height: avgHeight };
    });

    converged = centroids.every(
      (centroid, i) => distance2D(centroid, newCentroids[i]) < 0.001
    );

    centroids = newCentroids;
    iterations++;
  }

  const clusterObjects = clusters
    .map((members, index) => {
      if (members.length === 0) return null;

      const centroid = {
        width: Math.round(centroids[index].width * 10) / 10,
        height: Math.round(centroids[index].height * 10) / 10,
      };

      const widths = members.map((m) => m.width);
      const heights = members.map((m) => m.height);

      return {
        centroid: centroid,
        members: members,
        count: members.length,
        minWidth: Math.min(...widths),
        maxWidth: Math.max(...widths),
        minHeight: Math.min(...heights),
        maxHeight: Math.max(...heights),
        label: formatDimensionLabel(centroid),
      };
    })
    .filter((cluster) => cluster !== null);

  return {
    clusters: clusterObjects,
    centroids: clusterObjects.map((c) => c.centroid),
  };
};

const distance2D = (p1, p2) => {
  return Math.sqrt(
    Math.pow(p1.width - p2.width, 2) + Math.pow(p1.height - p2.height, 2)
  );
};

const initializeCentroids2D = (dimensions, k) => {
  if (k >= dimensions.length) {
    return dimensions.slice();
  }

  const centroids = [];

  centroids.push(dimensions[Math.floor(Math.random() * dimensions.length)]);

  for (let i = 1; i < k; i++) {
    const distances = dimensions.map((dim) => {
      return Math.min(
        ...centroids.map((centroid) => distance2D(dim, centroid))
      );
    });

    const squaredDistances = distances.map((d) => d * d);
    const totalSquaredDistance = squaredDistances.reduce(
      (sum, d) => sum + d,
      0
    );

    if (totalSquaredDistance === 0) {
      const remainingDims = dimensions.filter(
        (dim) =>
          !centroids.some((centroid) => distance2D(dim, centroid) < 1e-10)
      );
      if (remainingDims.length > 0) {
        centroids.push(
          remainingDims[Math.floor(Math.random() * remainingDims.length)]
        );
      }
    } else {
      const probabilities = squaredDistances.map(
        (d) => d / totalSquaredDistance
      );

      const random = Math.random();
      let cumulativeProbability = 0;

      for (let j = 0; j < dimensions.length; j++) {
        cumulativeProbability += probabilities[j];
        if (random <= cumulativeProbability) {
          centroids.push(dimensions[j]);
          break;
        }
      }
    }
  }

  return centroids;
};

const formatDimensionLabel = (centroid) => {
  const w = centroid.width.toFixed(1);
  const h = centroid.height.toFixed(1);
  return `${w} × ${h}`;
};

export const windowBelongsToAreaCluster = (windowArea, cluster) => {
  if (!cluster || typeof windowArea !== "number") return false;

  return windowArea >= cluster.min && windowArea <= cluster.max;
};

export const windowBelongsToDimensionCluster = (
  windowWidth,
  windowHeight,
  cluster
) => {
  if (
    !cluster ||
    typeof windowWidth !== "number" ||
    typeof windowHeight !== "number"
  )
    return false;

  return (
    windowWidth >= cluster.minWidth &&
    windowWidth <= cluster.maxWidth &&
    windowHeight >= cluster.minHeight &&
    windowHeight <= cluster.maxHeight
  );
};

export const findWindowAreaCluster = (windowArea, clusters) => {
  if (!Array.isArray(clusters) || typeof windowArea !== "number") return null;

  return (
    clusters.find((cluster) =>
      windowBelongsToAreaCluster(windowArea, cluster)
    ) || null
  );
};

export const findWindowDimensionCluster = (
  windowWidth,
  windowHeight,
  clusters
) => {
  if (
    !Array.isArray(clusters) ||
    typeof windowWidth !== "number" ||
    typeof windowHeight !== "number"
  )
    return null;

  return (
    clusters.find((cluster) =>
      windowBelongsToDimensionCluster(windowWidth, windowHeight, cluster)
    ) || null
  );
};

export const calculateArea = (polygonString) => {
  try {
    if (!polygonString || typeof polygonString !== "string") {
      return 0;
    }

    const coordsString = polygonString.replace(/[[\]]/g, "");
    const coords = coordsString.split(",").map(Number);

    if (coords.some(isNaN)) {
      return 0;
    }

    const points = [];
    for (let i = 0; i < coords.length; i += 3) {
      if (i + 2 < coords.length) {
        points.push({
          x: coords[i],
          y: coords[i + 1],
          z: coords[i + 2],
        });
      }
    }

    const uniquePoints = [];
    const tolerance = 1e-10;

    for (let i = 0; i < points.length; i++) {
      const current = points[i];

      const isDuplicate = uniquePoints.some(
        (existingPoint) =>
          Math.abs(existingPoint.x - current.x) < tolerance &&
          Math.abs(existingPoint.y - current.y) < tolerance &&
          Math.abs(existingPoint.z - current.z) < tolerance
      );

      if (!isDuplicate) {
        uniquePoints.push(current);
      }
    }

    if (uniquePoints.length < 3) {
      return 0;
    }

    let totalArea = 0;
    const n = uniquePoints.length;

    const origin = uniquePoints[0];

    for (let i = 1; i < n - 1; i++) {
      const p1 = uniquePoints[i];
      const p2 = uniquePoints[i + 1];

      const v1 = {
        x: p1.x - origin.x,
        y: p1.y - origin.y,
        z: p1.z - origin.z,
      };

      const v2 = {
        x: p2.x - origin.x,
        y: p2.y - origin.y,
        z: p2.z - origin.z,
      };

      const cross = {
        x: v1.y * v2.z - v1.z * v2.y,
        y: v1.z * v2.x - v1.x * v2.z,
        z: v1.x * v2.y - v1.y * v2.x,
      };

      const magnitude = Math.sqrt(
        cross.x * cross.x + cross.y * cross.y + cross.z * cross.z
      );
      totalArea += magnitude / 2;
    }

    return totalArea;
  } catch (error) {
    return 0;
  }
};

export const kMeansClustering = (areas, k) => {
  if (!Array.isArray(areas) || areas.length === 0) {
    return { clusters: [], centroids: [] };
  }

  const tolerance = 1e-1;
  const uniqueAreas = [];
  for (let i = 0; i < areas.length; i++) {
    const current = areas[i];
    const isDuplicate = uniqueAreas.some(
      (existing) => Math.abs(existing - current) < tolerance
    );

    if (!isDuplicate) {
      uniqueAreas.push(current);
    }
  }

  if (uniqueAreas.length <= 1) {
    return {
      clusters: [{ centroid: uniqueAreas[0] || 0, members: areas }],
      centroids: [uniqueAreas[0] || 0],
    };
  }

  const reasonableK = Math.min(k, uniqueAreas.length);

  let centroids = initializeCentroidsKMeansPlusPlus(uniqueAreas, reasonableK);

  let clusters = [];
  let iterations = 0;
  const maxIterations = 100;
  let converged = false;

  while (!converged && iterations < maxIterations) {
    clusters = Array.from({ length: reasonableK }, () => []);

    for (const area of areas) {
      let nearestCentroidIndex = 0;
      let minDistance = Math.abs(area - centroids[0]);

      for (let i = 1; i < centroids.length; i++) {
        const distance = Math.abs(area - centroids[i]);
        if (distance < minDistance) {
          minDistance = distance;
          nearestCentroidIndex = i;
        }
      }

      clusters[nearestCentroidIndex].push(area);
    }

    const newCentroids = clusters.map((cluster) => {
      if (cluster.length === 0) return 0;
      return cluster.reduce((sum, area) => sum + area, 0) / cluster.length;
    });

    converged = centroids.every(
      (centroid, i) => Math.abs(centroid - newCentroids[i]) < 0.001
    );

    centroids = newCentroids;
    iterations++;
  }

  const clusterObjects = clusters
    .map((members, index) => ({
      centroid: Math.round(centroids[index] * 10) / 10,
      members: members,
      count: members.length,
      min: members.length > 0 ? Math.min(...members) : 0,
      max: members.length > 0 ? Math.max(...members) : 0,
    }))
    .filter((cluster) => cluster.count > 0);

  return {
    clusters: clusterObjects,
    centroids: clusterObjects.map((c) => c.centroid),
  };
};

const initializeCentroidsKMeansPlusPlus = (areas, k) => {
  if (k >= areas.length) {
    return areas.slice();
  }

  const centroids = [];

  centroids.push(areas[Math.floor(Math.random() * areas.length)]);

  for (let i = 1; i < k; i++) {
    const distances = areas.map((area) => {
      return Math.min(
        ...centroids.map((centroid) => Math.abs(area - centroid))
      );
    });

    const squaredDistances = distances.map((d) => d * d);
    const totalSquaredDistance = squaredDistances.reduce(
      (sum, d) => sum + d,
      0
    );

    if (totalSquaredDistance === 0) {
      const remainingAreas = areas.filter((area) => !centroids.includes(area));
      if (remainingAreas.length > 0) {
        centroids.push(
          remainingAreas[Math.floor(Math.random() * remainingAreas.length)]
        );
      }
    } else {
      const probabilities = squaredDistances.map(
        (d) => d / totalSquaredDistance
      );

      const random = Math.random();
      let cumulativeProbability = 0;

      for (let j = 0; j < areas.length; j++) {
        cumulativeProbability += probabilities[j];
        if (random <= cumulativeProbability) {
          centroids.push(areas[j]);
          break;
        }
      }
    }
  }

  return centroids;
};
